import React, { useState, useEffect } from 'react';
import { updateProject } from '../../services/projectService';
import './ProjectDetails.css';

/**
 * ProjectDetails component for editing project metadata
 * @param {Object} props Component props
 * @param {Object} props.project Current project data
 * @param {Function} props.onUpdate Callback when project is updated
 * @param {Function} props.onClose Optional callback when form is closed
 */
const ProjectDetails = ({ project, onUpdate, onClose }) => {
  const [formData, setFormData] = useState({
    customer: project?.customer || '',
    location: project?.location || '',
    installation_requirements: project?.installation_requirements || {
      unionLabor: false,
      fullInstall: true, // Cable + Last Mile
      selfInstallInfrastructure: false, // Only relevant if fullInstall is false
      selfInstallWaffle: false, // For waffle ceiling self-installation
      afterHoursOnly: false, // For after hours work
      conduitRequired: false,
      jboxRequired: false,
      racewayConfirmed: true,
      bespokeDetails: '',
      quoteQAd: false
    }
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle checkbox changes for installation requirements
  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      installation_requirements: {
        ...prev.installation_requirements,
        [name]: checked
      }
    }));
  };

  // Handle text input changes for installation requirements
  const handleInstallationTextChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      installation_requirements: {
        ...prev.installation_requirements,
        [name]: value
      }
    }));
  };

  // Save changes
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      // We only want to update the specific fields we're editing
      const { data, error } = await updateProject(project.id, {
        customer: formData.customer,
        location: formData.location,
        installation_requirements: formData.installation_requirements
      });

      if (error) {
        throw new Error(error);
      }
      
      if (data && onUpdate) {
        // If the update was successful and we have data, update the UI
        const updatedProject = {
          ...project,
          customer: formData.customer,
          location: formData.location,
          installation_requirements: formData.installation_requirements
        };
        onUpdate(updatedProject);
      }
      
      if (onClose) onClose();
    } catch (err) {
      console.error('Error updating project:', err);
      setError(err.message || 'Failed to update project details');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="project-details">
      <h2>Project Details</h2>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="customer">Customer</label>
          <input
            type="text"
            id="customer"
            name="customer"
            value={formData.customer}
            onChange={handleChange}
            placeholder="Enter customer name or organization"
          />
        </div>

        <div className="form-group">
          <label htmlFor="location">Location</label>
          <input
            type="text"
            id="location"
            name="location"
            value={formData.location}
            onChange={handleChange}
            placeholder="Enter project location"
          />
        </div>

        <div className="form-group">
          <label className="section-label">Installation Requirements</label>
          
          <div className="checkbox-group">
            <div className="checkbox-item">
              <input
                type="checkbox"
                id="unionLabor"
                name="unionLabor"
                checked={formData.installation_requirements.unionLabor}
                onChange={handleCheckboxChange}
              />
              <label htmlFor="unionLabor">Union Labor Required</label>
            </div>

            <div className="checkbox-item">
              <input
                type="checkbox"
                id="fullInstall"
                name="fullInstall"
                checked={formData.installation_requirements.fullInstall}
                onChange={handleCheckboxChange}
              />
              <label htmlFor="fullInstall">Full Installation (Cable + Last Mile)</label>
            </div>

            {!formData.installation_requirements.fullInstall && (
              <div className="checkbox-item nested">
                <input
                  type="checkbox"
                  id="selfInstallInfrastructure"
                  name="selfInstallInfrastructure"
                  checked={formData.installation_requirements.selfInstallInfrastructure}
                  onChange={handleCheckboxChange}
                />
                <label htmlFor="selfInstallInfrastructure">
                  Customer's cable vendor will install infrastructure
                </label>
              </div>
            )}

            <div className="checkbox-item">
              <input
                type="checkbox"
                id="selfInstallWaffle"
                name="selfInstallWaffle"
                checked={formData.installation_requirements.selfInstallWaffle}
                onChange={handleCheckboxChange}
              />
              <label htmlFor="selfInstallWaffle">Self-install (Waffle)</label>
            </div>

            <div className="checkbox-item">
              <input
                type="checkbox"
                id="afterHoursOnly"
                name="afterHoursOnly"
                checked={formData.installation_requirements.afterHoursOnly}
                onChange={handleCheckboxChange}
              />
              <label htmlFor="afterHoursOnly">After hours work only</label>
            </div>

            <div className="checkbox-item">
              <input
                type="checkbox"
                id="conduitRequired"
                name="conduitRequired"
                checked={formData.installation_requirements.conduitRequired}
                onChange={handleCheckboxChange}
              />
              <label htmlFor="conduitRequired">Conduit Required</label>
            </div>

            <div className="checkbox-item">
              <input
                type="checkbox"
                id="jboxRequired"
                name="jboxRequired"
                checked={formData.installation_requirements.jboxRequired}
                onChange={handleCheckboxChange}
              />
              <label htmlFor="jboxRequired">J-Box Required</label>
            </div>

            <div className="checkbox-item">
              <input
                type="checkbox"
                id="quoteQAd"
                name="quoteQAd"
                checked={formData.installation_requirements.quoteQAd}
                onChange={handleCheckboxChange}
              />
              <label htmlFor="quoteQAd">Quote has been QA'd</label>
            </div>

            <div className="checkbox-item">
              <input
                type="checkbox"
                id="racewayConfirmed"
                name="racewayConfirmed"
                checked={formData.installation_requirements.racewayConfirmed}
                onChange={handleCheckboxChange}
              />
              <label htmlFor="racewayConfirmed">
                For Waffle Ceiling: Standard Raceway is Sufficient
              </label>
            </div>

            {!formData.installation_requirements.racewayConfirmed && (
              <div className="nested-input">
                <label htmlFor="bespokeDetails">Bespoke Installation Details:</label>
                <textarea
                  id="bespokeDetails"
                  name="bespokeDetails"
                  value={formData.installation_requirements.bespokeDetails || ''}
                  onChange={handleInstallationTextChange}
                  placeholder="Please provide details about bespoke installation requirements"
                />
                <div className="note">
                  Note: This will require us to connect with the customer for more information before providing a price.
                </div>
              </div>
            )}
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="form-actions">
          <button type="submit" disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
          {onClose && (
            <button type="button" onClick={onClose}>
              Cancel
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default ProjectDetails; 